import React, { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import gsap from 'gsap';
import { useTranslation } from 'react-i18next';
import './CinematicLoader.css';

const SIGNALS = [
  { x: 160, y: 36 }, { x: 248, y: 72 }, { x: 284, y: 160 },
  { x: 248, y: 248 }, { x: 160, y: 284 }, { x: 72, y: 248 },
  { x: 36, y: 160 }, { x: 72, y: 72 },
];

export const CinematicLoader: React.FC<{ progress?: number; isUpdate?: boolean; message?: string }> = ({
  progress,
  isUpdate = false,
  message,
}) => {
  const { t, i18n } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const measured = typeof progress === 'number' && Number.isFinite(progress) && progress > 0;
  const value = measured ? Math.min(100, Math.max(0, progress)) : 0;
  const status = message || (isUpdate
    ? t('loader.optimizing_protocol', { defaultValue: 'Optimizing Protocol Version' })
    : t('loader.syncing_grid', { defaultValue: 'Syncing Regional Price Grid' }));

  useEffect(() => {
    if (reduceMotion) return;
    const ctx = gsap.context(() => {
      gsap.fromTo('.loader-lens', { opacity: 0, scale: 0.82, rotation: -8 },
        { opacity: 1, scale: 1, rotation: 0, duration: 1.1, ease: 'expo.out' });
      gsap.fromTo('.loader-reveal', { opacity: 0, y: 12 },
        { opacity: 1, y: 0, duration: 0.65, stagger: 0.1, delay: 0.15, ease: 'power3.out' });
    }, containerRef);
    return () => ctx.revert();
  }, [reduceMotion]);

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 1 }}
      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -12 }}
      transition={{ duration: reduceMotion ? 0.15 : 0.35 }}
      className="cinematic-loader font-outfit"
      aria-busy="true"
    >
      <div className="loader-grid" aria-hidden="true" />
      <div className="loader-ambient" aria-hidden="true" />
      <div className="loader-content">
        <div className="loader-lens" aria-hidden="true">
          <div className="loader-halo" />
          <svg className="loader-network" viewBox="0 0 320 320" fill="none">
            <circle cx="160" cy="160" r="124" stroke="currentColor" strokeOpacity="0.12" />
            <circle cx="160" cy="160" r="92" stroke="currentColor" strokeOpacity="0.12" strokeDasharray="2 8" />
            <path d="M72 72H248V248H72Z M36 160H284 M160 36V284 M72 72L248 248 M248 72L72 248" stroke="currentColor" strokeOpacity="0.09" />
            {SIGNALS.map(({ x, y }, i) => <g key={i} className="loader-signal" style={{ animationDelay: `${i * -0.4}s` }}>
              <circle cx={x} cy={y} r="8" fill="currentColor" fillOpacity="0.07" />
              <circle cx={x} cy={y} r="2.5" fill="currentColor" />
            </g>)}
          </svg>
          <div className="loader-orbit"><span /></div>
          <div className="loader-orbit loader-orbit-inner"><span /></div>
          <div className="loader-mark">
            <img src="/logo.svg" alt="" width="96" height="96" fetchPriority="high" />
            <div className="loader-scan" />
          </div>
          <span className="loader-corner loader-corner-tl" />
          <span className="loader-corner loader-corner-tr" />
          <span className="loader-corner loader-corner-bl" />
          <span className="loader-corner loader-corner-br" />
        </div>
        <div className="loader-identity loader-reveal">
          <div className="loader-wordmark">{t('app.title', { defaultValue: 'CartLens' })}<span aria-hidden="true">.</span></div>
          <p className="loader-status" role="status" aria-live="polite">{status}</p>
        </div>
        <div className="loader-meter-group loader-reveal">
          <div className={`loader-meter ${measured ? '' : 'loader-meter-indeterminate'}`}
            role="progressbar" aria-label={status}
            aria-valuemin={0} aria-valuemax={100} aria-valuenow={measured ? Math.round(value) : undefined}>
            <div className="loader-meter-fill" style={measured ? { width: `${value}%` } : undefined} />
          </div>
          <div className="loader-meter-caption">
            <span className="loader-activity" aria-hidden="true"><i /><i /><i /></span>
            <span>{isUpdate
              ? t('loader.applying_intelligence', { defaultValue: 'Applying Neural Intelligence' })
              : t('loader.connecting_dna', { defaultValue: 'Connecting Spending DNA' })}</span>
            {measured && <span className="loader-percent" aria-hidden="true">{new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 0 }).format(value / 100)}</span>}
          </div>
        </div>
      </div>
      <p className="loader-signature loader-reveal">{t('app.tagline', { defaultValue: 'Precision. Optimization. Intelligence.' })}</p>
    </motion.div>
  );
};

export default CinematicLoader;
